import { motion, type Variants } from 'framer-motion'
import { useState, useEffect } from 'react'

import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { 
  CheckCircle, 
  ArrowLeft, 
  Loader2, 
  AlertCircle,
  CreditCard,
  Hash,
  XCircle
} from 'lucide-react'
import { QRCodeComponent } from '@/components/ui/qr-code'


import { useSystemDefaults } from '@/state/system'
import { CopyButton } from '@/components/ui/shadcn-io/copy-button'
import { useTransactions } from '@/state/transactions'
import { useMutationTracker } from '@/hooks/useMutationTracker'
import { useTranslation } from '../../../hooks/useTranslation'

// Animation variants
const containerVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.4,
      ease: 'easeOut'
    } 
  },
}

const cardVariants: Variants = {
  hidden: { opacity: 0, scale: 0.95 },
  show: { 
    opacity: 1, 
    scale: 1,
    transition: { 
      type: 'spring',
      stiffness: 300,
      damping: 25,
      delay: 0.2
    } 
  },
}

export default function ConfirmPurchasePage() {
  const { t } = useTranslation()
  const [transactionId, setTransactionId] = useState('')
  const navigate = useNavigate()
  const search = useSearch({ from: '/dashboard/services/confirm-purchase' })
  const pendingTransactionId = (search as { transaction_id?: string }).transaction_id
  const {getWalletAddress} = useSystemDefaults()

  const {completePurchase, cancelPurchase} = useTransactions()

  const confirmState = useMutationTracker('complete-purchase');
  const cancelState = useMutationTracker('cancel-purchase');

  // Use useEffect to handle navigation after mutation success
  useEffect(() => {
    if (confirmState.isSuccess) {
      navigate({ to: '/dashboard/funds/transactions' })
    }
  }, [confirmState.isSuccess, navigate])

  useEffect(() => {
    if (cancelState.isSuccess) {
      navigate({ to: '/dashboard/services/browse' })
    }
  }, [cancelState.isSuccess, navigate])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (transactionId.trim()) {
      completePurchase({txId:transactionId.trim(), transaction_id: Number (pendingTransactionId || -1)})
    }
  }

  const handleCancel = () => {
    const txId = pendingTransactionId
    if (txId) {
      cancelPurchase({transaction_id: Number(pendingTransactionId || -1)})
    }
  }

  const handleBack = () => {
    navigate({ to: '..' })
  }

  if (confirmState.isSuccess) {
    return (
      <motion.div 
        initial="hidden" 
        animate="show" 
        variants={containerVariants}
        className="flex items-center justify-center min-h-[60vh]"
      >
        <motion.div variants={cardVariants} className="text-center">
          <div className="h-20 w-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-green-400 to-green-600 flex items-center justify-center">
            <CheckCircle className="h-10 w-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-foreground mb-4">
            {t('pages.confirmPurchase.confirmed')}
          </h2>
          <p className="text-lg text-muted-foreground mb-6">
            {t('pages.confirmPurchase.confirmedMessage')}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('pages.confirmPurchase.redirecting')}
          </p>
        </motion.div>
      </motion.div>
    )
  }

  return (
    <motion.div 
      initial="hidden" 
      animate="show" 
      variants={containerVariants}
      className="max-w-2xl mx-auto space-y-8"
    >
      {/* Header */}
      <div className="space-y-4">
        <Button
          variant="ghost"
          onClick={handleBack}
          className="text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.buttons.back')}
        </Button>
        
        <div className="text-center">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-green-100 to-blue-100 dark:from-green-900/20 dark:to-blue-900/20 border border-green-200 dark:border-green-800 mb-4">
            <span className="text-sm font-medium text-green-700 dark:text-green-300">
              {t('pages.confirmPurchase.confirmPurchase')}
            </span>
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">
            {t('pages.confirmPurchase.title')}
          </h1>
          <p className="text-lg text-muted-foreground mt-2">
            {t('pages.confirmPurchase.subtitle')}
          </p>
        </div>
      </div>

      {/* Confirm Purchase Form */}
      <motion.div variants={cardVariants}>
        <Card className="bg-white/80 dark:bg-background/80 backdrop-blur-sm border-0 shadow-xl">
          <CardHeader className="text-center pb-6">
            <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
              <CreditCard className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl text-foreground">
              {t('pages.confirmPurchase.paymentVerification')}
            </CardTitle>
            <p className="text-muted-foreground">
              {t('pages.confirmPurchase.provideTransactionId')}
            </p>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className='flex justify-center' >
              <QRCodeComponent value={String(getWalletAddress())}  />
            </div>

            <div className='flex flex-row justify-center items-center' >
              <div className='flex flex-row px-3 py-1 bg-card rounded-3xl justify-center items-center  ' >

                <p className="text-sm text-muted-foreground font-mono break-all" >
                  {getWalletAddress()}
                </p>
                <CopyButton variant={'default'} className='bg-black' content={String(getWalletAddress())} />
              </div>

            </div>

            <p className="text-sm text-muted-foreground text-center">
              {t('pages.confirmPurchase.scanQrCode')}
            </p>

            {/* Transaction ID Display (if provided in URL) */}
            {pendingTransactionId && (
              <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2 mb-2">
                  <Hash className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    {t('pages.confirmPurchase.pendingTransaction')}
                  </span>
                </div>
                <div className='flex flex-row space-x-4' >
                  <p className="text-sm text-blue-700 dark:text-blue-300 font-mono">
                    {pendingTransactionId}
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    {t('pages.confirmPurchase.useThisOrDifferent')}
                  </p>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label 
                  htmlFor="transactionId" 
                  className="text-sm font-medium text-foreground"
                >
                   {t('pages.confirmPurchase.txid')}
                </label>
                <Input
                  id="transactionId"
                  type="text"
                  placeholder={t('pages.confirmPurchase.enterTxId')}
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  className="h-12 text-lg font-mono"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  {t('pages.confirmPurchase.txIdHelp')}
                </p>
              </div>

              {/* Error Messages */}
              {Boolean(confirmState.error || cancelState.error) && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
                >
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                    <span className="text-sm font-medium text-red-900 dark:text-red-100">
                      {confirmState.error ? t('pages.confirmPurchase.confirmationFailed') : t('pages.confirmPurchase.cancellationFailed')}
                    </span>
                  </div>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                    {(() => {
                      const error = confirmState.error || cancelState.error;
                      if (error instanceof Error) {
                        return error.message;
                      }
                      return confirmState.error 
                        ? t('pages.confirmPurchase.confirmPaymentFailed')
                        : t('pages.confirmPurchase.cancelTransactionFailed');
                    })()}
                  </p>
                </motion.div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Cancel Button */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={(!transactionId.trim() && !pendingTransactionId) || cancelState.isPending || confirmState.isPending}
                  className="w-full sm:w-auto h-12 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-800 dark:hover:text-red-200 hover:border-red-400 dark:hover:border-red-600"
                >
                  {cancelState.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('pages.confirmPurchase.cancelling')}
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 mr-2" />
                      {t('pages.confirmPurchase.cancelTransaction')}
                    </>
                  )}
                </Button>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={!transactionId.trim() || confirmState.isPending || cancelState.isPending}
                  className="w-full sm:flex-1 h-12 text-slate-900 font-semibold"
                >
                  {confirmState.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('pages.confirmPurchase.confirmingPayment')}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      {t('pages.confirmPurchase.confirmPurchaseButton')}
                    </>
                  )}
                </Button>
              </div>
            </form>

            {/* Help Text */}
                <div className="text-center">
              <p className="text-xs text-muted-foreground">
                {t('pages.confirmPurchase.helpText')}
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  )
}
